import axios, { Method } from "axios";
import sleep from "./utils/sleep";
import { ClientOptions, SharedClient } from "./Client";
import { HTTP_REFRESH_HEADER, REFRESH_TOKEN_ROUTE } from "./settings";
import { getDateTime, isDateExpiredNow } from "./utilities";

let refresh: Promise<void> | null = null;
class Authenticator {
  private access: string;
  private accessExpAt: string;
  private client: SharedClient;
  private options: ClientOptions;

  constructor(client: SharedClient, options: ClientOptions) {
    this.client = client;
    this.access = "";
    this.accessExpAt = "";
    this.options = options;
  }

  public get refresh(): Promise<void> | null {
    return refresh;
  }

  public set accessToken(value: string) {
    this.access = value;
  }

  public get accessToken() {
    return this.access;
  }

  public get accessExp() {
    return this.accessExpAt;
  }

  public get hasAccessToken() {
    let result = Boolean(this.access) && !this.isExpired;
    return result;
  }

  public get isExpired() {
    try {
      const expiry = getDateTime(this.accessExpAt);

      // take safety margin of 1000ms
      if (!expiry || isDateExpiredNow(expiry, 1000)) {
        this.access = ""; // clean access token
        return true;
      }

      return false;
    } catch (error) {
      console.error("[client.authenticator] failed to check access token exp");
      this.access = "";
      return true;
    }
  }

  public clear() {
    this.access = "";
    this.client.post("/auth/logout", {}).catch((e) => {});
    if (this.options.secureStorage) {
      try {
        this.options.secureStorage
          .setItem(HTTP_REFRESH_HEADER, "")
          .catch((error) => {
            console.error(
              "failed to clear refresh token from secure store",
              error
            );
          });
      } catch (error) {
        console.error("failed to clear authenticator", error);
      }
    }
  }

  async setSecureTokens({
    accessExpAt,
    access,
    refresh,
  }: {
    accessExpAt: string;
    access: string;
    refresh?: string;
  }) {
    this.accessExpAt = accessExpAt;
    this.accessToken = access;

    if (refresh && refresh.length > 0 && this.options.secureStorage) {
      try {
        const currentRefresh = await this.options.secureStorage.getItem(
          HTTP_REFRESH_HEADER
        );

        // update refresh only if they are diff and payload refresh exist
        if (currentRefresh !== refresh) {
          await this.options.secureStorage.setItem(
            HTTP_REFRESH_HEADER,
            refresh
          );
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  public async asyncGetRefreshToken(retry = 0) {
    let refresh: string = "";

    if (this.options.secureStorage) {
      try {
        refresh = await this.options.secureStorage.getItem(HTTP_REFRESH_HEADER);
        return refresh;
      } catch (error) {
        console.error("failed to retrive refresh token from secure store");
        console.error(error);
        if (retry > 3) return refresh;
        await sleep(200);
        return this.asyncGetRefreshToken(retry++);
      }
    }

    return refresh;
  }

  public async refreshToken(): Promise<void> {
    if (refresh) return refresh;

    refresh = new Promise(async (resolve, reject) => {
      try {
        const res = await this.client({
          url: REFRESH_TOKEN_ROUTE.path,
          method: REFRESH_TOKEN_ROUTE.method as Method,
        });

        await this.setSecureTokens({
          access: res.data.access,
          refresh: res.data.refresh,
          accessExpAt: res.data.accessExpAt,
        });

        this.options.onRefreshSuccess?.(res.data);

        resolve();
        refresh = null;
      } catch (error) {
        // throw user on failed refresh try
        if (axios.isAxiosError(error)) {
          if (
            error.response &&
            error.response.status > 399 &&
            error.response.status < 499
          ) {
            // client related; network error wont cause logout;
            console.error(
              "[client.authenticator] failed refreshing, logging out",
              error
            );
            this.client.logout();
          }
        }

        // clear exist access token
        this.clear();
        refresh = null;
        reject(error);
      }

      return refresh;
    });

    return refresh;
  }
}

export default Authenticator;
